import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import jwt_decode from 'jwt-decode';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  // Base URL without /api - used specifically for auth endpoints
  const AUTH_URL = 'https://multichannel-ai-integration.techspawn.workers.dev';

  useEffect(() => {
    // Check if user is logged in on initial load
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwt_decode(token);
        // Check if token is expired
        if (decoded.exp * 1000 < Date.now()) {
          console.log('Token expired, logging out');
          localStorage.removeItem('token');
          setCurrentUser(null);
        } else {
          console.log('Valid token found, setting user', decoded);
          setCurrentUser({
            id: decoded.userId,
            name: decoded.name,
            email: decoded.email,
            role: decoded.role
          });
          // Set default auth header for all requests
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Error parsing token:', error);
        localStorage.removeItem('token');
        setCurrentUser(null);
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    console.log(`Attempting login with email: ${email}`);
    setAuthError(null);
    
    try {
      // Use the native fetch API with explicit URL
      const response = await fetch(`${AUTH_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });
      
      console.log('Login response status:', response.status);
      
      // Try to parse JSON even if it fails
      let data;
      try {
        data = await response.json();
        console.log('Login response data:', data);
      } catch (e) {
        console.error('Error parsing response:', e);
        console.log('Response text:', await response.text());
        throw new Error('Invalid server response format');
      }
      
      // Check for error in response
      if (!data.success) {
        setAuthError(data.message || 'Login failed');
        return false;
      }
      
      if (data.token) {
        localStorage.setItem('token', data.token);
        
        // Decode and set user data
        const decoded = jwt_decode(data.token);
        console.log("Decoded token:", decoded);
        
        setCurrentUser({
          id: decoded.userId || decoded.id,
          name: decoded.name,
          email: decoded.email,
          role: decoded.role
        });
        
        // Set default auth header for subsequent requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
        return true;
      }
      
      setAuthError('Invalid response from server - missing token');
      return false;
    } catch (error) {
      console.error('Login error:', error);
      setAuthError('Network error or server unavailable. Please try again.');
      return false;
    }
  };

  const register = async (name, email, password, planId) => {
    console.log(`Attempting registration for email: ${email}`);
    setAuthError(null);
    try {
      const response = await fetch(`${AUTH_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ name, email, password, plan_id: planId })
      });
      console.log('Registration response status:', response.status);
      let data;
      try {
        data = await response.json();
        console.log('Registration response data:', data);
      } catch (e) {
        console.error('Error parsing response:', e);
        console.log('Response text:', await response.text());
        throw new Error('Invalid server response format');
      }
      if (data.success) {
        return {
          success: true,
          message: data.message || 'Registration successful',
          redirect: data.redirect,
          plan_id: data.plan_id
        };
      } else {
        setAuthError(data.message || 'Registration failed');
        return {
          success: false,
          message: data.message || 'Registration failed'
        };
      }
    } catch (error) {
      console.error('Register error:', error);
      setAuthError('Network error or server unavailable. Please try again.');
      return {
        success: false,
        message: 'Network error or server unavailable. Please try again.'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setCurrentUser(null);
    setAuthError(null);
  };

  // Check if user is superadmin
  const isSuperAdmin = () => {
    return currentUser?.role === 'superadmin';
  };

  // Value to provide
  const value = {
    currentUser,
    login,
    register,
    logout,
    isSuperAdmin,
    authError,
    clearAuthError: () => setAuthError(null)
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
